import Image from "next/image";
import Logo from "+/davis-construction.webp";
import Link from "next/link";
import {
  ClockIcon,
  EnvelopeIcon,
  MapIcon,
  PhoneArrowDownLeftIcon,
} from "@heroicons/react/24/solid";
import { getAllServices, getAllCities } from "@/lib/data";
import { SITE_URL, toAbsoluteUrl } from "@/lib/env";

/**
 * Footer component with comprehensive SEO optimization
 * Includes service links, city links, NAP information, and structured data
 */
export default function Footer() {
  const services = getAllServices();
  const cities = getAllCities();
  
  // Get popular cities for quick links (first 8 cities alphabetically)
  const popularCities = cities.slice(0, 8);
  
  // Business information for NAP consistency
  const businessInfo = {
    name: "Davis Construction Group",
    address: "9116 Griffin Road, Cooper City, Florida 33328",
    phone: "(954) 689-0320",
    email: "office@davisconstructiongrp.com",
    hours: "Mon - Fri 9AM - 5PM",
    licenses: "LQ23291 - CGC1520093 - CFC1427299 - LI35053",
  };

  return (
    <footer className="border-t border-border py-12 md:py-16 px-5 bg-gradient-to-b from-background to-background/95">
      <div className="max-w-7xl mx-auto">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 mb-12">
          {/* Company Info Section */}
          <div className="space-y-4">
            <Link href="/" className="inline-block">
              <Image
                src={Logo}
                width={150}
                height={60}
                quality={75}
                sizes="(max-width: 768px) 150px, 150px"
                alt="Davis Construction Group - Licensed Construction Services in Broward County, FL"
                className="mx-auto md:mx-0"
                priority
              />
            </Link>
            <p className="max-w-[280px] font-light text-sm text-white/80 leading-relaxed">
              Family-owned construction company serving Broward County, Florida for over 50 years. 
              Licensed, insured, and trusted for septic systems, propane installations, home additions, 
              and general construction services.
            </p>
            <div className="pt-2">
              <Link 
                href="/contact" 
                className="inline-block px-6 py-2 bg-yellow text-background font-semibold rounded-md hover:bg-yellow/90 transition-colors"
                aria-label="Contact Davis Construction Group"
              >
                Get Free Quote
              </Link>
            </div>
          </div>

          {/* Services Section */}
          <div>
            <h3 className="mb-6 text-lg font-semibold text-yellow">Our Services</h3>
            <nav aria-label="Service pages">
              <ul className="space-y-3">
                {services.map((service) => (
                  <li key={service.slug}>
                    <Link 
                      href={`/services/${service.slug}`}
                      className="text-white/80 hover:text-yellow hover:underline text-sm transition-colors"
                      aria-label={`${service.name} services in Broward County`}
                    >
                      {service.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            
            {/* Popular Service-City Links */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <h4 className="mb-3 text-sm font-semibold text-white/90">Popular Locations</h4>
              <ul className="space-y-2">
                {services.slice(0, 3).map((service) => (
                  <li key={`${service.slug}-popular`}>
                    <Link
                      href={`/services/${service.slug}/${popularCities[0]?.slug || 'cooper-city'}`}
                      className="text-white/70 hover:text-yellow text-xs hover:underline transition-colors"
                    >
                      {service.name} in {popularCities[0]?.name || 'Cooper City'}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Quick Links Section */}
          <div>
            <h3 className="mb-6 text-lg font-semibold text-yellow">Quick Links</h3>
            <nav aria-label="Site navigation">
              <ul className="space-y-3">
                <li>
                  <Link 
                    href="/" 
                    className="text-white/80 hover:text-yellow hover:underline text-sm transition-colors"
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/broward-county-services" 
                    className="text-white/80 hover:text-yellow hover:underline text-sm transition-colors"
                  >
                    Broward County Services
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/contact" 
                    className="text-white/80 hover:text-yellow hover:underline text-sm transition-colors"
                  >
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link 
                    href="/sitemap.xml" 
                    className="text-white/80 hover:text-yellow hover:underline text-sm transition-colors"
                  >
                    Sitemap
                  </Link>
                </li>
              </ul>
            </nav>

            {/* City Links */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <h4 className="mb-3 text-sm font-semibold text-white/90">Service Areas</h4>
              <ul className="grid grid-cols-2 gap-2">
                {popularCities.map((city) => (
                  <li key={city.slug}>
                    <Link
                      href={`/services/${services[0]?.slug || 'septic-systems'}/${city.slug}`}
                      className="text-white/70 hover:text-yellow text-xs hover:underline transition-colors"
                      aria-label={`Services in ${city.name}, FL`}
                    >
                      {city.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Information Section */}
          <div>
            <h3 className="mb-6 text-lg font-semibold text-yellow">Contact Information</h3>
            <div className="space-y-4 text-white/80 text-sm">
              {/* Phone */}
              <div className="flex items-start gap-3">
                <PhoneArrowDownLeftIcon
                  width={20}
                  height={20}
                  className="text-yellow flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div>
                  <p className="font-medium text-white">Phone</p>
                  <a 
                    href={`tel:${businessInfo.phone.replace(/\D/g, '')}`}
                    className="hover:text-yellow transition-colors"
                    aria-label={`Call ${businessInfo.name} at ${businessInfo.phone}`}
                  >
                    {businessInfo.phone}
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-3">
                <EnvelopeIcon 
                  width={20} 
                  height={20} 
                  className="text-yellow flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div>
                  <p className="font-medium text-white">Email</p>
                  <a 
                    href={`mailto:${businessInfo.email}`}
                    className="hover:text-yellow transition-colors break-all"
                    aria-label={`Email ${businessInfo.name} at ${businessInfo.email}`}
                  >
                    {businessInfo.email}
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start gap-3">
                <ClockIcon 
                  width={20} 
                  height={20} 
                  className="text-yellow flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div>
                  <p className="font-medium text-white">Business Hours</p>
                  <p>{businessInfo.hours}</p>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-3">
                <MapIcon 
                  width={20} 
                  height={20} 
                  className="text-yellow flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div>
                  <p className="font-medium text-white">Address</p>
                  <address className="not-italic">
                    <a
                      href="https://maps.google.com/?q=9116+Griffin+Road+Cooper+City+FL+33328"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-yellow transition-colors"
                      aria-label={`View ${businessInfo.name} location on Google Maps`}
                    >
                      {businessInfo.address}
                    </a>
                  </address>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-white/70">
            <div>
              <p className="mb-2">
                &copy; {new Date().getFullYear()} {businessInfo.name}. All rights reserved.
              </p>
              <p className="text-xs">
                Licenses: {businessInfo.licenses}
              </p>
            </div>
            <div className="flex flex-wrap gap-4 justify-center md:justify-end">
              <Link 
                href="/contact" 
                className="hover:text-yellow transition-colors"
              >
                Privacy Policy
              </Link>
              <span className="text-white/30">|</span>
              <Link 
                href="/contact" 
                className="hover:text-yellow transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Structured Data for Local Business */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${SITE_URL}/#organization`,
            "name": businessInfo.name,
            "image": toAbsoluteUrl("/davis-construction.webp"),
            "logo": toAbsoluteUrl("/davis-construction.webp"),
            "url": SITE_URL,
            "telephone": businessInfo.phone,
            "email": businessInfo.email,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "9116 Griffin Road",
              "addressLocality": "Cooper City",
              "addressRegion": "FL",
              "postalCode": "33328",
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "26.0579",
              "longitude": "-80.2719"
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": [
                "Monday",
                "Tuesday",
                "Wednesday",
                "Thursday",
                "Friday"
              ],
              "opens": "09:00",
              "closes": "17:00"
            },
            "priceRange": "$$",
            "areaServed": {
              "@type": "City",
              "name": "Broward County"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Construction Services",
              "itemListElement": services.map((service) => ({
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": service.name,
                  "description": service.shortDescription,
                  "url": toAbsoluteUrl(`/services/${service.slug}`)
                }
              }))
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "reviewCount": "127"
            }
          })
        }}
      />
    </footer>
  );
}
